/**
 * Sign up page – email/password + display name. Uses auth use case.
 */
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { signUp } from '../../domain/usecases/auth';
import Card from '../../shared/ui/Card.jsx';
import Input from '../../shared/ui/Input.jsx';
import Button from '../../shared/ui/Button.jsx';
import Display from '../../shared/ui/Display.jsx';
import Body from '../../shared/ui/Body.jsx';
import styles from './AuthPages.module.css';

export function SignUpPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/';
  const referredBy = searchParams.get('ref') || '';

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signUp(email, password, displayName, { referredBy });
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message || 'Sign up failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.wrap}>
      <Card padding="lg" className={styles.card}>
        <img src="/logo.png" alt="BarChain" className={styles.logo} />
        <Display as="h1" size="l" style={{ textAlign: 'center', marginBottom: '0.25rem' }}>
          BarChain
        </Display>
        <Body tone="secondary" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          Create your athlete profile
        </Body>
        {referredBy && (
          <Body tone="secondary" style={{ textAlign: 'center', marginBottom: '1rem', color: 'var(--gold)' }}>
            Invited by @{referredBy.toLowerCase()} — you'll both get Pro free once you log your first PR.
          </Body>
        )}

        <form onSubmit={handleSubmit} className={styles.form}>
          <Input
            label="DISPLAY NAME"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoComplete="name"
          />
          <Input
            label="EMAIL"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <Input
            label="PASSWORD"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            autoComplete="new-password"
            help="Minimum 6 characters"
          />
          {error && (
            <Body tone="secondary" style={{ color: 'var(--red-platform)' }}>
              {error}
            </Body>
          )}
          <Button
            variant="primary"
            size="lg"
            type="submit"
            disabled={loading}
            style={{ width: '100%' }}
          >
            {loading ? 'Creating account…' : 'Sign up'}
          </Button>
        </form>

        <div className={styles.footer}>
          <Button
            variant="ghost"
            size="md"
            as={Link}
            to={redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login'}
          >
            I already have an account
          </Button>
        </div>
      </Card>
    </div>
  );
}
